import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const api = {
    // Upload
    uploadImage: (file) => {
        const formData = new FormData();
        formData.append('file', file);
        return axios.post(`${API}/upload/image`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        });
    },
    
    // Recipes
    getRecipes: (params = {}) => axios.get(`${API}/recipes`, { params }),
    getFeaturedRecipes: () => axios.get(`${API}/recipes/featured`),
    getRecipe: (id) => axios.get(`${API}/recipes/${id}`),
    createRecipe: (data) => axios.post(`${API}/recipes`, data),
    updateRecipe: (id, data) => axios.put(`${API}/recipes/${id}`, data),
    deleteRecipe: (id) => axios.delete(`${API}/recipes/${id}`),
    getMyRecipes: () => axios.get(`${API}/my-recipes`),
    getRecipesUsingThis: (id) => axios.get(`${API}/recipes/${id}/used-in`),
    searchRecipesForLinking: (query) => axios.get(`${API}/recipes/search-for-linking`, { params: { q: query } }),
    
    // Comments & Ratings
    getRecipeComments: (recipeId, skip = 0, limit = 20) => axios.get(`${API}/recipes/${recipeId}/comments`, { params: { skip, limit } }),
    createComment: (recipeId, data) => axios.post(`${API}/recipes/${recipeId}/comments`, data),
    updateComment: (recipeId, commentId, data) => axios.put(`${API}/recipes/${recipeId}/comments/${commentId}`, data),
    deleteComment: (recipeId, commentId) => axios.delete(`${API}/recipes/${recipeId}/comments/${commentId}`),
    getRecipeRating: (recipeId) => axios.get(`${API}/recipes/${recipeId}/rating`),
    getMyComments: (skip = 0, limit = 20) => axios.get(`${API}/my-comments`, { params: { skip, limit } }),
    
    // User Preferences
    getUserPreferences: () => axios.get(`${API}/user/preferences`),
    updateUserPreferences: (prefs) => axios.put(`${API}/user/preferences`, prefs),
    
    // Password Reset
    forgotPassword: (email) => axios.post(`${API}/auth/forgot-password`, { email }),
    resetPassword: (token, newPassword) => axios.post(`${API}/auth/reset-password`, { token, new_password: newPassword }),
    verifyResetToken: (token) => axios.get(`${API}/auth/verify-reset-token`, { params: { token } }),
    changePassword: (currentPassword, newPassword) => axios.post(`${API}/auth/change-password`, { current_password: currentPassword, new_password: newPassword }),
    
    // Tips
    getTips: (params = {}) => axios.get(`${API}/tips`, { params }),
    getTip: (id) => axios.get(`${API}/tips/${id}`),
    createTip: (data) => axios.post(`${API}/tips`, data),
    
    // Deals
    getDeals: (params = {}) => axios.get(`${API}/deals`, { params }),
    getFeaturedDeals: () => axios.get(`${API}/deals/featured`),
    
    // Weekly Pack
    getWeeklyPack: () => axios.get(`${API}/weekly-pack/current`),
    getWeeklyPackById: (id) => axios.get(`${API}/weekly-pack/${id}`),
    getWeeklyPacksHistory: (skip = 0, limit = 12) => axios.get(`${API}/weekly-packs/history`, { params: { skip, limit } }),
    downloadPackPdf: async (packId = 'current') => {
        const response = await axios.get(`${API}/weekly-pack/${packId}/pdf`, {
            responseType: 'blob'
        });
        // Create download link
        const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `pack_${packId}.pdf`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        return response;
    },
    
    // Admin
    checkAdmin: () => axios.get(`${API}/auth/is-admin`),
    getAdminStats: () => axios.get(`${API}/admin/stats`),
    
    // Admin - Users
    getAdminUsers: () => axios.get(`${API}/admin/users`),
    giveUserPremium: (userId, durationDays = null) => axios.put(`${API}/admin/users/${userId}/premium`, { duration_days: durationDays }),
    revokeUserPremium: (userId) => axios.put(`${API}/admin/users/${userId}/revoke-premium`),
    
    // Admin - Admin Management
    getAdminList: () => axios.get(`${API}/admin/admins`),
    makeUserAdmin: (userId) => axios.put(`${API}/admin/users/${userId}/make-admin`),
    removeUserAdmin: (userId) => axios.put(`${API}/admin/users/${userId}/remove-admin`),
    
    // Admin - Backup
    createBackup: () => axios.post(`${API}/admin/backup`),
    listBackups: () => axios.get(`${API}/admin/backups`),
    restoreBackup: (filename) => axios.post(`${API}/admin/restore/${filename}`),
    
    // Admin - Packs
    getAdminPacks: () => axios.get(`${API}/admin/weekly-packs`),
    createAdminPack: (data) => axios.post(`${API}/admin/weekly-packs`, data),
    updateAdminPack: (id, data) => axios.put(`${API}/admin/weekly-packs/${id}`, data),
    deleteAdminPack: (id) => axios.delete(`${API}/admin/weekly-packs/${id}`),
    togglePackPublish: (id) => axios.put(`${API}/admin/weekly-packs/${id}/publish`),
    sendPackNotification: (id) => axios.post(`${API}/admin/weekly-packs/${id}/notify`),
    generateGroceryList: (id) => axios.post(`${API}/admin/weekly-packs/${id}/generate-grocery-list`),
    generateRecommendations: (id) => axios.post(`${API}/admin/weekly-packs/${id}/generate-recommendations`),
    getGroceryListWithDeals: (id) => axios.get(`${API}/admin/weekly-packs/${id}/grocery-list`),
    updateGroceryList: (id, groceryList) => axios.put(`${API}/admin/weekly-packs/${id}/grocery-list`, { grocery_list: groceryList }),
    updateGroceryItemDeal: (packId, item, dealId) => axios.put(`${API}/admin/weekly-packs/${packId}/grocery-item`, { item, deal_id: dealId }),
    
    // Admin - Recipes
    getAdminRecipes: (status = null) => axios.get(`${API}/admin/recipes`, { params: status ? { status } : {} }),
    createAdminRecipe: (data) => axios.post(`${API}/admin/recipes`, data),
    updateAdminRecipe: (id, data) => axios.put(`${API}/admin/recipes/${id}`, data),
    approveRecipe: (id) => axios.post(`${API}/admin/recipes/${id}/approve`),
    rejectRecipe: (id) => axios.put(`${API}/admin/recipes/${id}/reject`),
    deleteAdminRecipe: (id) => axios.delete(`${API}/admin/recipes/${id}`),
    importRecipeFromUrl: (url) => axios.post(`${API}/admin/recipes/import-from-url`, { url, auto_calculate_costs: true }),
    
    // User - Personal Packs (Premium)
    generatePersonalPack: (criteria) => axios.post(`${API}/user/generate-personal-pack`, criteria),
    getPersonalPacks: (limit = 5) => axios.get(`${API}/user/personal-packs`, { params: { limit } }),
    
    // Admin - Tips
    getAdminTips: (status = null) => axios.get(`${API}/admin/tips`, { params: status ? { status } : {} }),
    createAdminTip: (data) => axios.post(`${API}/admin/tips`, data),
    updateAdminTip: (id, data) => axios.put(`${API}/admin/tips/${id}`, data),
    approveTip: (id) => axios.put(`${API}/admin/tips/${id}/approve`),
    rejectTip: (id) => axios.put(`${API}/admin/tips/${id}/reject`),
    deleteAdminTip: (id) => axios.delete(`${API}/admin/tips/${id}`),
    
    // Admin - Deals
    getAdminDeals: () => axios.get(`${API}/admin/deals`),
    createAdminDeal: (data) => axios.post(`${API}/admin/deals`, data),
    updateAdminDeal: (id, data) => axios.put(`${API}/admin/deals/${id}`, data),
    deleteAdminDeal: (id) => axios.delete(`${API}/admin/deals/${id}`),
    toggleDealPublish: (id) => axios.put(`${API}/admin/deals/${id}/publish`),
    publishAllDeals: () => axios.put(`${API}/admin/deals/publish-all`),
    unpublishAllDeals: () => axios.put(`${API}/admin/deals/unpublish-all`),
    
    // Admin - Dossiers spéciaux
    getAdminDossiers: () => axios.get(`${API}/admin/dossiers`),
    createAdminDossier: (data) => axios.post(`${API}/admin/dossiers`, data),
    updateAdminDossier: (id, data) => axios.put(`${API}/admin/dossiers/${id}`, data),
    deleteAdminDossier: (id) => axios.delete(`${API}/admin/dossiers/${id}`),
    
    // Public - Dossiers
    getDossiers: () => axios.get(`${API}/dossiers`),
    getDossier: (slug) => axios.get(`${API}/dossiers/${slug}`),
    
    // Favorites
    getFavorites: () => axios.get(`${API}/favorites`),
    addFavorite: (recipeId) => axios.post(`${API}/favorites/${recipeId}`),
    removeFavorite: (recipeId) => axios.delete(`${API}/favorites/${recipeId}`),
    
    // User Grocery List
    getUserGroceryList: (packId) => axios.get(`${API}/user/grocery-list/${packId}`),
    updateUserGroceryList: (packId, data) => axios.put(`${API}/user/grocery-list/${packId}`, data),
    resetUserGroceryList: (packId) => axios.post(`${API}/user/grocery-list/${packId}/reset`),
    checkFavorite: (recipeId) => axios.get(`${API}/favorites/check/${recipeId}`),
    
    // Ingredient Substitution (Premium)
    getSubstitutionSuggestions: (ingredientName, reason = null) => 
        axios.post(`${API}/user/ingredient-substitution/suggest`, { ingredient_name: ingredientName, reason }),
    substituteIngredient: (packId, data) => axios.post(`${API}/user/grocery-list/${packId}/substitute`, data),
    getUserSubstitutions: (packId) => axios.get(`${API}/user/substitutions/${packId}`),
    
    // Subscriptions
    getPlans: () => axios.get(`${API}/subscriptions/plans`),
    createCheckout: (planId, originUrl) => axios.post(`${API}/subscriptions/checkout`, { plan_id: planId, origin_url: originUrl }),
    getCheckoutStatus: (sessionId) => axios.get(`${API}/subscriptions/status/${sessionId}`),
    getTrialStatus: () => axios.get(`${API}/subscriptions/trial-status`),
    
    // AI
    suggestRecipe: (data) => axios.post(`${API}/ai/suggest-recipe`, data),
    suggestTip: () => axios.post(`${API}/ai/suggest-tip`),
    suggestMealPlan: (data) => axios.post(`${API}/ai/meal-plan`, data),
    improveRecipe: (recipeId) => axios.post(`${API}/ai/improve-recipe`, { recipe_id: recipeId }),
    
    // Admin AI Generation
    generateRecipes: (count = 5) => axios.post(`${API}/admin/ai/generate-recipes`, { count }),
    generateTips: (count = 5) => axios.post(`${API}/admin/ai/generate-tips`, { count }),
    
    // Ingredient Prices
    getIngredientPrice: (name) => axios.get(`${API}/ingredients/price`, { params: { name } }),
    searchIngredients: (q) => axios.get(`${API}/ingredients/search`, { params: { q } }),
    estimateIngredientPrice: (ingredient, quantity) => axios.post(`${API}/ingredients/estimate-price`, { ingredient, quantity }),
    
    // Categories
    getCategories: () => axios.get(`${API}/categories`),
    
    // Stats
    getStats: () => axios.get(`${API}/stats`),
    
    // Seed
    seedData: () => axios.post(`${API}/seed`),
};

export default api;
